import React, {
  createContext,
  useEffect,
  useState,
  useContext,
  useCallback,
  useMemo,
} from "react";

type Theme = "light" | "dark";

type ThemeContextType = {
  theme: Theme;
  setTheme: (theme: Theme) => void;
};

const ThemeContext = createContext<ThemeContextType>({
  theme: "light",
  setTheme: () => {},
});

export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const prefersDarkOS = useMediaQuery('(prefers-color-scheme: dark)');
  const [mounted, setMounted] = useState(false);

  // Carrega preferência do usuário (localStorage) ou usa SO
  const getInitialTheme = (): Theme => {
    if (typeof window === "undefined") return "light";
    try {
      const stored = window.localStorage.getItem("theme");
      if (stored === "light" || stored === "dark") return stored;
      return prefersDarkOS ? "dark" : "light";
    } catch {
      return "light";
    }
  };

  const [theme, setThemeState] = useState<Theme>(
    typeof window === "undefined" ? "light" : getInitialTheme
  );

  // Atualiza localStorage e html[data-theme] ao trocar tema
  const setTheme = useCallback((newTheme: Theme) => {
    setThemeState(newTheme);
    if (typeof window !== "undefined") {
      localStorage.setItem("theme", newTheme);
      document.documentElement.setAttribute("data-theme", newTheme);
    }
  }, []);

  // Sincroniza com SO caso user não tenha override manual
  useEffect(() => {
    const stored = localStorage.getItem("theme");
    if (!stored) setTheme(prefersDarkOS ? "dark" : "light");
    // eslint-disable-next-line
  }, [prefersDarkOS]);

  // Ao montar, garante que o atributo data-theme está correto
  useEffect(() => {
    document.documentElement.setAttribute("data-theme", theme);
  }, [theme]);

  useEffect(() => {
    setMounted(true);
  }, []);

  const contextValue = useMemo(() => ({ theme, setTheme }), [theme, setTheme]);

  if (!mounted) {
    // Evita renderizar até saber o tema correto no cliente
    return null;
  }

  return (
    <ThemeContext.Provider value={contextValue}>
      {children}
    </ThemeContext.Provider>
  );
};

export function useThemeContext() {
  return useContext(ThemeContext);
}

// Hook otimizado
function useMediaQuery(query: string): boolean {
  const getMatches = () => typeof window !== "undefined" && window.matchMedia(query).matches;
  const [matches, setMatches] = useState(getMatches);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const media = window.matchMedia(query);
    if (media.matches !== matches) setMatches(media.matches);

    const listener = () => setMatches(media.matches);
    media.addEventListener("change", listener);
    return () => media.removeEventListener("change", listener);
    // eslint-disable-next-line
  }, [query]);

  return matches;
}